import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import qs from 'qs';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import './login.css';
import { AUTH_TOKEN_KEYS, EXTERNAL_LINKS, ROLE } from '../../../../shared/constants/common';
import { FEATURED_POSTS } from '../../../../shared/constants/data';
import { fetchUser, updateUser } from '../../../../shared/actions/userActions';
import { fetchCreator } from '../../../../shared/actions/creatorActions';
import { loginUser } from '../../../../shared/actions/authActions';
import { setLocalItem, getLocalItem, clearLocalStorage, removeLocalItem } from '../../../../shared/utils/config/localStorage';
import { createLoginFormSchema } from '../../../../shared/validations/login';
import { getRedirectPath } from '../../../../shared/utils/function';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { clearNavigationState } from '../../../../shared/utils/navigationState';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from "../../../../shared/components/form";
import FeaturePost from '../../../../features/cards/FeaturePost';
import HeaderSection from '../../../../components/layout/header';
import SocialInfo from '../../../component/SocialInfo';
import { showError } from '../../../../shared/utils/swalHandler';
import { reportWarning } from '../../../../shared/sentry';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import AddToHomeScreenModal from '../../../../views/Modals/AddToHomeScreen';

// Constants
const STORAGE_KEYS = {
  ADD_TO_HOME_SCREEN_SHOWN: 'addToHomeScreenShown',
  ACCESS_TOKEN: 'accessToken',
  ROLE: 'role',
};

const ERROR_MESSAGES = {
  NO_USER_DATA: "No user data received from login API",
  NO_ACCESS_TOKEN: "No access token received from login API",
  ACCOUNT_DELETED: "Account deleted or doesn't exist!",
  LOGIN_FAILED: "Login failed. Please try again.",
  UNEXPECTED_ERROR: "An unexpected error occurred. Please try again.",
};

const LOGIN_ERROR_TITLES = {
  DEFAULT: 'Login Failed',
  ACCOUNT_LOCKED: 'Account Locked 🔒',
};

/**
 * ModernLogin Component
 * Handles user authentication and login flow
 */
const ModernLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const form = useForm({
    resolver: zodResolver(createLoginFormSchema()),
    defaultValues: {
      email: '',
      password: ''
    },
    mode: "onChange",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [showAddToHomeScreen, setShowAddToHomeScreen] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);

  // Extract redirect URL from query parameters
  useEffect(() => {
    const queryParams = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    if (queryParams.redirectUrl) {
      setRedirectUrl(queryParams.redirectUrl);
    }
  }, []);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (showAddToHomeScreen) {
      return;
    }

    const accessToken = getLocalItem(STORAGE_KEYS.ACCESS_TOKEN);
    const role = getLocalItem(STORAGE_KEYS.ROLE);

    if (accessToken && accessToken !== "undefined") {
      const calculatedPath = getRedirectPath(role);
      navigate.to(calculatedPath);
    }
  }, [navigate, showAddToHomeScreen]);

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  /**
   * Stores user data in localStorage
   * @param {Object} user - User object from API
   * @param {string} accessToken - Authentication token
   */
  const storeUserData = useCallback((user, accessToken) => {
    // Set before accessToken so push sync skips support-password sessions on auth events.
    if (user.isLoginWithSupportPassword) {
      setLocalItem(AUTH_TOKEN_KEYS.IS_LOGIN_WITH_SUPPORT_PASSWORD, true);
    } else {
      removeLocalItem(AUTH_TOKEN_KEYS.IS_LOGIN_WITH_SUPPORT_PASSWORD);
    }

    // Write identity fields before accessToken — accessToken dispatches localStorageChange
    // and triggers FCM sync; userId must exist first (config signature + API auth context).
    setLocalItem("userId", user._id);
    setLocalItem(STORAGE_KEYS.ROLE, user.rol);
    setLocalItem("email", user.email);
    setLocalItem("account_verified", user.account_verified);
    setLocalItem("send_notification", user.send_notification);
    setLocalItem("username", user.username);
    setLocalItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  }, []);

  /**
   * Fetches additional user data based on role
   * @param {Object} user - User object from API
   */
  const fetchUserDataByRole = useCallback((user) => {
    dispatch(fetchUser());
    if (user.rol === ROLE.CREATOR_SUB || user.rol === ROLE.CREATOR_CONTENT) {
      dispatch(fetchCreator(user.username));
    }
  }, [dispatch]);

  /**
   * Handles navigation after successful login
   * @param {string} userRole - User's role
   */
  const handlePostLoginNavigation = useCallback((userRole, isShowWarningHomeModal = false, isLoginWithSupportPassword = false) => {
    const calculatedPath = getRedirectPath(userRole, redirectUrl);
    setRedirectPath(calculatedPath);

    if (isLoginWithSupportPassword || isShowWarningHomeModal) {
      navigate.to(calculatedPath);
    } else {
      setShowAddToHomeScreen(true);
    }
  }, [navigate, redirectUrl]);

  /**
   * Extracts URL from error message if it contains an anchor tag
   * @param {string} errorText - Error message text
   * @returns {string|null} - Extracted URL or null
   */
  const extractUrlFromError = useCallback((errorText) => {
    const linkMatch = errorText.match(/<a[^>]+href=["']([^"']+)["'][^>]*>/i);
    return linkMatch ? linkMatch[1] : null;
  }, []);

  /**
   * Handles error display with optional redirect
   * @param {string} errorText - Error message to display
   * @param {string|null} extractedUrl - Optional URL to redirect to
   */
  const handleLoginError = useCallback(async (errorText, extractedUrl, title = LOGIN_ERROR_TITLES.DEFAULT) => {
    await showError(title, errorText, {
      // didClose: () => {
      //   if (extractedUrl) {
      //     const isExternalUrl = extractedUrl.startsWith('http://') || extractedUrl.startsWith('https://');
      //     if (isExternalUrl) {
      //       window.location.href = extractedUrl;
      //     } else {
      //       navigate.to(extractedUrl);
      //     }
      //   }
      // }
    });
  }, [navigate]);

  /**
   * Validates login response data
   * @param {Object} result - Login API response
   * @returns {Object} - Validated user and accessToken
   * @throws {Error} - If validation fails
   */
  const validateLoginResponse = useCallback((result) => {
    if (!result.success) {
      throw new Error(result.error || ERROR_MESSAGES.LOGIN_FAILED);
    }

    const { user, accessToken } = result.data;

    if (!user) {
      throw new Error(ERROR_MESSAGES.NO_USER_DATA);
    }

    if (!accessToken) {
      throw new Error(ERROR_MESSAGES.NO_ACCESS_TOKEN);
    }

    if (user.disabled) {
      clearLocalStorage();
      throw new Error(ERROR_MESSAGES.ACCOUNT_DELETED);
    }

    return { user, accessToken };
  }, []);

  /**
   * Handles form submission
   * @param {Object} data - Form data containing email and password
   */
  const onSubmit = async (data) => {
    setIsLoading(true);

    const credentials = {
      email: data.email,
      password: data.password,
    };

    try {
      const result = await dispatch(loginUser(credentials));

      if (!result.success) {
        const errorText = result.error || ERROR_MESSAGES.LOGIN_FAILED;
        const extractedUrl = extractUrlFromError(errorText);
        const errorTitle = result.exceptionName === 'LoginRestricted'
          ? LOGIN_ERROR_TITLES.ACCOUNT_LOCKED
          : LOGIN_ERROR_TITLES.DEFAULT;
        await handleLoginError(errorText, extractedUrl, errorTitle);
        return;
      }

      const { user, accessToken } = validateLoginResponse(result);

      clearNavigationState();
      fetchUserDataByRole(user);
      storeUserData(user, accessToken);
      handlePostLoginNavigation(
        user.rol,
        user?.isShowWarningHomeModal,
        user?.isLoginWithSupportPassword,
      );
    } catch (error) {
      const errorText = error?.message || ERROR_MESSAGES.UNEXPECTED_ERROR;
      const extractedUrl = extractUrlFromError(errorText);
      await handleLoginError(errorText, extractedUrl);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles forgot password navigation
   */
  const handleForgotPassword = useCallback(() => {
    navigate.toForgotPassword();
  }, [navigate]);

  /**
   * Handles create account navigation
   */
  const handleCreateAccount = useCallback(() => {
    navigate.toSignup();
  }, [navigate]);

  /**
   * Handles back navigation
   */
  const handleBack = useCallback(() => {
    navigate.toDiscover();
  }, [navigate]);

  /**
   * Handles AddToHomeScreen modal close and navigation
   */
  const handleAddToHomeScreenClose = useCallback(async () => {
    setShowAddToHomeScreen(false);

    if (!getLocalItem(AUTH_TOKEN_KEYS.IS_LOGIN_WITH_SUPPORT_PASSWORD)) {
      try {
        await dispatch(updateUser({ isShowWarningHomeModal: true }));
        await dispatch(fetchUser());
      } catch (error) {
        reportWarning(error, 'ModernLogin update isShowWarningHomeModal');
      }
    }

    if (redirectPath) {
      navigate.to(redirectPath);
    } else {
      const userRole = getLocalItem(STORAGE_KEYS.ROLE);
      const calculatedPath = getRedirectPath(userRole, redirectUrl);
      navigate.to(calculatedPath);
    }
  }, [dispatch, navigate, redirectPath, redirectUrl]);

  return (
    <>
      <div className={`modern-login-screen`}>
        {/* Header */}
        <HeaderSection
          type="image"
          handleBack={handleBack}
        />

        <main className="modern-login-main">
          <div className="modern-login-bg-effects">
            <div className="modern-login-bg-gradient"></div>
            <div className="modern-login-bg-blur-1"></div>
            <div className="modern-login-bg-blur-2"></div>
          </div>

          <div className="modern-login-container">
            <div className="modern-login-welcome">
              <h1 className="modern-login-welcome-title">Welcome Back</h1>
              <p className="modern-login-welcome-subtitle">Your content, your money. Keep 100%</p>
              <div className="modern-login-welcome-divider"></div>
            </div>

            <div className="modern-login-form-container">
              <div className="modern-login-form-wrapper">
                <Form {...form}>
                  <form className="modern-login-form" onSubmit={form.handleSubmit(onSubmit)}>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="modern-login-input-group">
                          <FormLabel className="modern-login-label">Email</FormLabel>
                          <div className="modern-login-input-wrapper mb-2">
                            <FormControl>
                              <input
                                type="text"
                                name="realEmail"
                                placeholder="Enter your email"
                                className="modern-login-input"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                                {...field}
                              />
                            </FormControl>
                            <div className="modern-login-input-icon">
                              <i className="far fa-user"></i>
                            </div>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="modern-login-input-group">
                          <FormLabel className="modern-login-label">Password</FormLabel>
                          <div className="modern-login-input-wrapper mb-2">
                            <FormControl>
                              <input
                                type={showPassword ? "text" : "password"}
                                className="modern-login-input"
                                placeholder="Enter your password"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                {...field}
                              />
                            </FormControl>
                            <div className="modern-login-input-icon">
                              <i className="fas fa-lock"></i>
                            </div>
                            <div
                              className="modern-login-password-toggle"
                              onClick={togglePasswordVisibility}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  togglePasswordVisibility();
                                }
                              }}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </div>
                          </div>
                          <div className="modern-login-forgot-password">
                            <button
                              type="button"
                              className="modern-login-forgot-btn"
                              onClick={handleForgotPassword}
                            >
                              <i className="fas fa-key"></i>
                              <span>Forgot Password?</span>
                            </button>
                          </div>
                        </FormItem>
                      )}
                    />

                    <button
                      type="submit"
                      className="modern-login-submit-btn"
                      disabled={isLoading || form.formState.isSubmitting}
                    >
                      <div className="modern-login-submit-content">
                        <i className="fas fa-right-to-bracket"></i>
                        <span>{isLoading || form.formState.isSubmitting ? 'Signing In...' : 'Sign In'}</span>
                      </div>
                      <div className="modern-login-submit-shine"></div>
                    </button>
                  </form>
                </Form>
              </div>
            </div>

            <div className="modern-login-alternative">
              <div className="modern-login-signup-link">
                <p className="modern-login-signup-text">
                  Don't have an account?
                  <span
                    className="modern-login-signup-link-text"
                    onClick={handleCreateAccount}
                  >
                    Create Account
                  </span>
                </p>
              </div>
            </div>

            <div className="modern-login-feature-posts">
              {FEATURED_POSTS.map((post, index) => (
                <FeaturePost key={index} post={post} index={index} />
              ))}
            </div>

            <div className="modern-login-footer-info">
              <div className="modern-login-billing-info">
                <span>For billing inquiries or to cancel your membership please visit </span>
                <span
                  className="modern-login-ccbill-link"
                  onClick={() => navigate.toExternal(EXTERNAL_LINKS.CCBILL)}
                >
                  {EXTERNAL_LINKS.CCBILL_TEXT}
                </span>
              </div>

              <div className="modern-login-divider"></div>
              <SocialInfo />
              <div className="modern-login-copyright mt-2">
                © {new Date().getFullYear()} Prestige International, LLC. All rights reserved.
              </div>
              <div className="modern-login-copyright">
                Juicy House®
              </div>
            </div>
          </div>
        </main>
      </div>

      <AddToHomeScreenModal
        isOpen={showAddToHomeScreen}
        onClose={handleAddToHomeScreenClose}
      />
    </>
  );
};

export default ModernLogin;
